import { useEffect, useState } from "react";
import { readCupsSold } from "../../data/local/shift";
import type { CupCount } from "../../domain/shift/cups";

/**
 * Cups sold this shift, by size — so the bar can see what has gone out and
 * restock the right sleeve before it runs dry.
 *
 * Read from the terminal's own sales, so it works offline and costs the server
 * nothing. It counts this till's shift only, and refreshes each time it opens.
 */

interface CupsCounterProps {
  shiftId: string;
  onClose(): void;
}

export function CupsCounter({ shiftId, onClose }: CupsCounterProps) {
  const [cups, setCups] = useState<{ sizes: CupCount[]; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    readCupsSold(shiftId)
      .then((result) => live && setCups(result))
      .catch((caught) => live && setError(caught instanceof Error ? caught.message : "Could not count"));
    return () => {
      live = false;
    };
  }, [shiftId]);

  return (
    <div className="shiftclose" role="dialog" aria-label="Cups sold this shift">
      <header className="shiftclose__head">
        <h2 className="shiftclose__title">Cups sold</h2>
        <p className="shiftclose__sub">This shift, on this till. Voided sales are left out.</p>
      </header>

      {error && <p className="shiftclose__error">{error}</p>}
      {!cups && !error && <p className="shiftclose__sub">Counting…</p>}
      {cups && cups.total === 0 && <p className="shiftclose__sub">No cups sold yet.</p>}
      {cups && cups.total > 0 && (
        <dl className="shiftclose__breakdown">
          {cups.sizes.map(({ size, count }) => (
            <div key={size}>
              <dt>{size}</dt>
              <dd>{count}</dd>
            </div>
          ))}
          <div className="shiftclose__breakdown-total">
            <dt>Total</dt>
            <dd>{cups.total}</dd>
          </div>
        </dl>
      )}

      <div className="shiftclose__actions">
        <button type="button" onClick={onClose}>
          Done
        </button>
      </div>
    </div>
  );
}

export default CupsCounter;
